import React, { useMemo } from 'react';
import { Box, Typography, Button, TextField, CircularProgress } from '@mui/material';
import { ChipInput, DateField, NumberField, StatusChip } from '../ui';
import { AutoAwesome, ExpandMore } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { CameraWithPricing, OverrideFormState, parseOverrideTagsInput } from '../../utils/productDetailHelpers';
import { getEffectiveMapForProduct } from '../../utils/mapPricing';
import { NeutralNote } from './NeutralNote';
import { StoreSpecsEditor, type StoreSpecsEditorProps } from './StoreSpecsEditor';
import { tokens } from '../../theme/tokens';
import type { StoreAiCopyDraft } from '../../services/api';
import type { StoreAiCopyUsage } from '../../services/storeAiCopyApi';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';

/**
 * The store's own AI copy (flag `store_ai_copy`, MOT-153): a Generate action
 * that writes a draft from the store's Copy rules, and the draft's review.
 * Absent while the flag is off, so the panel renders exactly as before.
 */
export interface ProductOverridesAiCopy {
  /** Owner or admin of the acting store (or a platform admin impersonating one): the routes refuse anyone else. */
  canManage: boolean;
  draft: StoreAiCopyDraft | null;
  usage: StoreAiCopyUsage | null;
  planIncluded: boolean;
  onGenerate: () => void;
  generatePending: boolean;
  onAccept: (fields?: Array<'title' | 'description'>) => void;
  acceptPending: boolean;
  onDiscard: () => void;
  discardPending: boolean;
  /** The budget 429 or another generation error, shown in place until the next try. */
  error: string | null;
}

/**
 * Approved bulk-AI spec suggestions for this product (flag `bulk_ai_operations`,
 * MOT-400), read-only beside the AI copy block. Use moves one into the store's
 * own `overrideSpecs`; Dismiss drops it. Null while the flag is off or when the
 * product has neither suggestions nor used specs.
 */
export interface ProductOverridesAiSpecs {
  suggestions: Record<string, string>;
  used: Record<string, string>;
  /** Owner or admin of the acting store: the routes refuse anyone else. */
  canManage: boolean;
  onUse: (label: string) => void;
  onDismiss: (label: string) => void;
  pendingLabel: string | null;
}

interface ProductOverridesSectionProps {
  product: CameraWithPricing;
  form: OverrideFormState;
  onFormChange: (updater: (prev: OverrideFormState) => OverrideFormState) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  canManageStoreOverrides: boolean;
  overrideLoading: boolean;
  onSave: () => void;
  savePending: boolean;
  onClear: () => void;
  clearPending: boolean;
  onGenerateDescription: () => void;
  generatePending: boolean;
  allowBelowMap: boolean;
  aiCopy?: ProductOverridesAiCopy | null;
  aiSpecs?: ProductOverridesAiSpecs | null;
  /** The store's own spec values editor (flag `store_override_specs_push`, MOT-413); null hides it. */
  ownSpecs?: StoreSpecsEditorProps | null;
}

const fieldSx = {
  width: '100%',
  boxSizing: 'border-box' as const,
  '& .MuiOutlinedInput-root': { borderRadius: '9px', fontSize: '13.5px' },
};

export function ProductOverridesSection({
  product,
  form,
  onFormChange,
  expanded,
  onToggleExpanded,
  canManageStoreOverrides,
  overrideLoading,
  onSave,
  savePending,
  onClear,
  clearPending,
  onGenerateDescription,
  generatePending,
  allowBelowMap,
  aiCopy,
  aiSpecs,
  ownSpecs,
}: ProductOverridesSectionProps) {
  const navigate = useNavigate();
  const { t } = useAppTranslation();

  const { enabled: keepRebateEnabled } = useOrgFeatureFlag('override_keeps_rebate');

  const setField = <K extends keyof OverrideFormState>(key: K, value: OverrideFormState[K]) =>
    onFormChange((prev) => ({ ...prev, [key]: value }));

  const fieldCount = [
    form.overrideTitle,
    form.overrideDescription,
    form.overrideTags,
    form.overridePrice,
    form.overridePriceStartAt,
    form.overridePriceEndAt,
    form.ignoreSalePrice ? '1' : '',
    form.ignoreRebate ? '1' : '',
    form.keepRebate ? '1' : '',
  ].filter(Boolean).length;

  const { floorViolation, floorLabel, floorBlocking } = useMemo(() => {
    const price = Number(form.overridePrice);
    const em = getEffectiveMapForProduct(product);
    const floor = em?.floor;
    const violates = !!(form.overridePrice && Number.isFinite(price) && floor && price > 0 && price < floor);
    const label =
      em && em.windowStatus === 'active' && em.afterIR != null
        ? `$${floor!.toFixed(2)} (MAP $${em.raw.toFixed(2)} − $${em.instantRebate.toFixed(0)} instant rebate)`
        : floor
          ? `$${floor.toFixed(2)}`
          : '';
    return { floorViolation: violates, floorLabel: label, floorBlocking: violates && !allowBelowMap };
  }, [form.overridePrice, product.pricing, product.pricingData, allowBelowMap]);

  // The floor check only looks at prices above zero, and the API accepts any
  // finite number, so a zero or negative override would otherwise save as a
  // MAP-safe price. Dates are yyyy-mm-dd here, so a string compare orders them.
  const overridePriceText = form.overridePrice.trim();
  const overridePriceNumber = Number(overridePriceText);
  const priceInvalid = overridePriceText !== '' && (!Number.isFinite(overridePriceNumber) || overridePriceNumber <= 0);
  const dateRangeInvalid = !!(
    form.overridePriceStartAt &&
    form.overridePriceEndAt &&
    form.overridePriceEndAt < form.overridePriceStartAt
  );
  const saveBlocked = floorBlocking || priceInvalid || dateRangeInvalid;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflow: 'hidden' }}>
      <Box
        component="button"
        onClick={onToggleExpanded}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', textAlign: 'left',
          p: '12px 14px', border: 'none', bgcolor: expanded ? tokens.surface.subtle : 'background.paper', cursor: 'pointer', font: 'inherit',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.primary' }}>Store overrides</Typography>
          <Typography sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>
            {fieldCount > 0 ? `${fieldCount} field${fieldCount === 1 ? '' : 's'} set` : 'Override title, description, tags and pricing for your store'}
          </Typography>
        </Box>
        {fieldCount > 0 && (
          <Box sx={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', height: 20, px: '8px', borderRadius: '6px', bgcolor: tokens.accent.tint, color: 'primary.dark', fontSize: '11.5px', fontWeight: 600 }}>
            {fieldCount} set
          </Box>
        )}
        <ExpandMore
          sx={{
            flex: '0 0 auto',
            fontSize: 18,
            color: 'text.secondary',
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </Box>

      {expanded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, p: '16px 14px', borderTop: `1px solid ${tokens.line.hairline}` }}>
          <Typography sx={{ fontSize: '12.5px', lineHeight: 1.55, color: tokens.ink.muted }}>
            Override title, description, tags and pricing for this product. These values sync to your commerce platform and win over the catalog.
          </Typography>
          {!canManageStoreOverrides && (
            <NeutralNote>Select a store organization via impersonation to view and edit overrides from admin.</NeutralNote>
          )}

          {aiCopy && <StoreAiCopyBlock aiCopy={aiCopy} canManageStoreOverrides={canManageStoreOverrides} overrideLoading={overrideLoading} />}

          {aiSpecs && <StoreAiSpecsBlock aiSpecs={aiSpecs} canManageStoreOverrides={canManageStoreOverrides} />}

          {ownSpecs && <StoreSpecsEditor {...ownSpecs} disabled={ownSpecs.disabled || !canManageStoreOverrides} />}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography component="label" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Title</Typography>
              <TextField
                size="small"
                value={form.overrideTitle}
                onChange={(e) => setField('overrideTitle', e.target.value)}
                disabled={!canManageStoreOverrides}
                placeholder={product.name || product.model}
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography component="label" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Description</Typography>
                <Button
                  size="small"
                  startIcon={generatePending ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: 13 }} />}
                  onClick={onGenerateDescription}
                  disabled={!canManageStoreOverrides || generatePending || overrideLoading}
                  sx={{ height: 26, px: '9px', borderRadius: '7px', border: '1px solid', borderColor: 'divider', color: 'primary.dark', textTransform: 'none', fontSize: '12px', fontWeight: 600 }}
                >
                  {generatePending ? 'Generating…' : 'Generate with AI'}
                </Button>
              </Box>
              <TextField
                size="small"
                multiline
                minRows={3}
                value={form.overrideDescription}
                onChange={(e) => setField('overrideDescription', e.target.value)}
                disabled={!canManageStoreOverrides}
                placeholder="Leave blank to use the catalog description"
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography component="label" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Tags</Typography>
              <ChipInput
                aria-label="Tags"
                value={parseOverrideTagsInput(form.overrideTags)}
                onChange={(tags) => setField('overrideTags', tags.join(', '))}
                disabled={!canManageStoreOverrides}
                placeholder="Leave blank to keep generated tags"
                sx={fieldSx}
              />
              <Typography sx={{ fontSize: '11.5px', lineHeight: 1.5, color: tokens.ink.muted }}>
                When set, replaces Motzu-generated Shopify tags for this product (motzu_synced may still be added).
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 1.75, borderTop: `1px solid ${tokens.line.hairline}` }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
              Price override
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.25 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography component="label" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Price</Typography>
                <NumberField
                  aria-label="Price"
                  value={form.overridePrice}
                  onChange={(v) => setField('overridePrice', v)}
                  disabled={!canManageStoreOverrides}
                  min={0}
                  step={0.01}
                  adornment="$"
                  error={floorBlocking || priceInvalid}
                  helperText={form.overridePrice ? undefined : 'Leave blank to use standard price'}
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography component="label" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Override start date</Typography>
                <DateField
                  aria-label="Override start date"
                  value={form.overridePriceStartAt}
                  onChange={(v) => setField('overridePriceStartAt', v)}
                  disabled={!canManageStoreOverrides}
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography component="label" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Override end date</Typography>
                <DateField
                  aria-label="Override end date"
                  value={form.overridePriceEndAt}
                  onChange={(v) => setField('overridePriceEndAt', v)}
                  disabled={!canManageStoreOverrides}
                  error={dateRangeInvalid}
                  sx={fieldSx}
                />
              </Box>
            </Box>

            {priceInvalid && (
              <NeutralNote>
                <strong>Enter a price above zero.</strong> A zero or negative override would be pushed to your storefront as-is.
              </NeutralNote>
            )}

            {dateRangeInvalid && (
              <NeutralNote>
                <strong>The end date is before the start date.</strong> An override with that window can never become active.
              </NeutralNote>
            )}

            {floorViolation && (
              floorBlocking ? (
                <NeutralNote>
                  <strong>Price below MAP is not allowed.</strong> Your override price is below the current advertised floor of {floorLabel}. To allow pricing below MAP, enable it in{' '}
                  <Typography
                    component="span"
                    sx={{ fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
                    onClick={() => navigate('/app/pricing-preferences')}
                  >
                    Pricing Preferences
                  </Typography>.
                </NeutralNote>
              ) : (
                <NeutralNote>
                  <strong>Caution:</strong> Your override price is below the current advertised floor of {floorLabel}. This may violate vendor agreements.
                </NeutralNote>
              )
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {([
                { key: 'ignoreSalePrice' as const, label: 'Ignore sale price', help: 'Push retail even while a sale is active.' },
                { key: 'ignoreRebate' as const, label: 'Ignore rebate', help: 'Push the pre-rebate price and skip the compare-at swap.' },
                ...(keepRebateEnabled
                  ? [{ key: 'keepRebate' as const, label: 'Keep rebates on my price', help: 'While a rebate runs, sell at your price minus the rebate. Otherwise sell at your price.' }]
                  : []),
              ]).map((t) => {
                const on = form[t.key];
                return (
                  <Box
                    key={t.key}
                    component="button"
                    onClick={() => {
                      if (!canManageStoreOverrides) return;
                      // Keeping and ignoring a rebate cannot both hold, so turning one on turns the other off.
                      onFormChange((prev) => ({
                        ...prev,
                        [t.key]: !on,
                        ...(!on && t.key === 'keepRebate' ? { ignoreRebate: false } : {}),
                        ...(!on && t.key === 'ignoreRebate' ? { keepRebate: false } : {}),
                      }));
                    }}
                    disabled={!canManageStoreOverrides}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', textAlign: 'left', border: 'none', bgcolor: 'transparent', p: 0, cursor: canManageStoreOverrides ? 'pointer' : 'default', font: 'inherit' }}
                  >
                    <Box sx={{ flex: '0 0 auto', position: 'relative', width: 34, height: 20, borderRadius: '10px', bgcolor: on ? 'primary.main' : 'divider', transition: 'background 120ms' }}>
                      <Box sx={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 16, height: 16, borderRadius: '8px', bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,0.2)', transition: 'left 120ms' }} />
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary' }}>{t.label}</Typography>
                      <Typography sx={{ fontSize: '12px', color: tokens.ink.muted }}>{t.help}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1.5, borderTop: `1px solid ${tokens.line.hairline}` }}>
            <Button
              onClick={onSave}
              disabled={!canManageStoreOverrides || savePending || clearPending || overrideLoading || saveBlocked}
              sx={{ height: 34, px: '14px', borderRadius: '9px', bgcolor: 'primary.main', color: 'common.white', textTransform: 'none', fontWeight: 600, fontSize: '13px', '&:hover': { bgcolor: 'primary.dark' } }}
            >
              Save overrides
            </Button>
            <Button
              onClick={onClear}
              disabled={!canManageStoreOverrides || clearPending || savePending || overrideLoading}
              sx={{ height: 34, px: '14px', borderRadius: '9px', border: '1px solid', borderColor: 'divider', color: 'text.secondary', textTransform: 'none', fontWeight: 600, fontSize: '13px' }}
            >
              Clear
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** The store's own AI copy draft and its actions (flag `store_ai_copy`); shared by both product details views. */
export function StoreAiCopyBlock({
  aiCopy,
  canManageStoreOverrides,
  overrideLoading,
}: {
  aiCopy: ProductOverridesAiCopy;
  canManageStoreOverrides: boolean;
  overrideLoading: boolean;
}) {
  const { t } = useAppTranslation();
  const aiCopyCanAct = canManageStoreOverrides && Boolean(aiCopy.canManage);
  return (

            <Box
              data-testid="store-ai-copy"
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '12px', borderRadius: '10px', border: `1px solid ${tokens.line.hairline}`, bgcolor: tokens.surface.subtle }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: 'text.primary' }}>{t('store.aiCopy.panel.title')}</Typography>
                  <Typography sx={{ fontSize: '11.5px', color: tokens.ink.muted }}>
                    {t('store.aiCopy.panel.intro')}
                    {aiCopy.usage && aiCopy.usage.limit !== -1
                      ? ` ${t('store.aiCopy.panel.usage', { used: aiCopy.usage.used, limit: aiCopy.usage.limit })}`
                      : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={aiCopy.generatePending ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: 13 }} />}
                  onClick={aiCopy.onGenerate}
                  disabled={!aiCopyCanAct || !aiCopy.planIncluded || aiCopy.generatePending || aiCopy.acceptPending || overrideLoading}
                  sx={{ height: 26, px: '9px', borderRadius: '7px', border: '1px solid', borderColor: 'divider', color: 'primary.dark', textTransform: 'none', fontSize: '12px', fontWeight: 600 }}
                >
                  {aiCopy.generatePending
                    ? t('store.aiCopy.panel.generating')
                    : aiCopy.draft
                      ? t('store.aiCopy.panel.generateAgain')
                      : t('store.aiCopy.panel.generate')}
                </Button>
              </Box>
              {!aiCopy.canManage && <NeutralNote>{t('store.aiCopy.panel.ownerOnly')}</NeutralNote>}
              {!aiCopy.planIncluded && <NeutralNote>{t('store.aiCopy.panel.planLocked')}</NeutralNote>}
              {aiCopy.error && <NeutralNote>{aiCopy.error}</NeutralNote>}
              {aiCopy.draft && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <StatusChip size="small" tone="info" label={t('store.aiCopy.panel.draftChip')} />
                    {aiCopy.draft.nearCopy && <StatusChip size="small" tone="caution" label={t('store.aiCopy.panel.nearCopyChip')} />}
                    {aiCopy.draft.bannedPhraseHits.length > 0 && (
                      <StatusChip size="small" tone="caution" label={t('store.aiCopy.panel.bannedChip', { phrases: aiCopy.draft.bannedPhraseHits.join(', ') })} />
                    )}
                  </Box>
                  {aiCopy.draft.title && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.faint }}>{t('store.aiCopy.panel.titleLabel')}</Typography>
                      <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>{aiCopy.draft.title}</Typography>
                    </Box>
                  )}
                  {aiCopy.draft.description && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.faint }}>{t('store.aiCopy.panel.descriptionLabel')}</Typography>
                      <Typography sx={{ fontSize: '12.5px', lineHeight: 1.55, color: 'text.primary', whiteSpace: 'pre-line' }}>{aiCopy.draft.description}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => aiCopy.onAccept()}
                      disabled={!aiCopyCanAct || aiCopy.acceptPending || aiCopy.discardPending || aiCopy.generatePending}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12px' }}
                    >
                      {aiCopy.acceptPending ? t('store.aiCopy.panel.accepting') : t('store.aiCopy.panel.accept')}
                    </Button>
                    {aiCopy.draft.title && aiCopy.draft.description && (
                      <>
                        <Button size="small" onClick={() => aiCopy.onAccept(['title'])} disabled={!aiCopyCanAct || aiCopy.acceptPending || aiCopy.discardPending || aiCopy.generatePending} sx={{ textTransform: 'none', fontSize: '12px' }}>
                          {t('store.aiCopy.panel.titleOnly')}
                        </Button>
                        <Button size="small" onClick={() => aiCopy.onAccept(['description'])} disabled={!aiCopyCanAct || aiCopy.acceptPending || aiCopy.discardPending || aiCopy.generatePending} sx={{ textTransform: 'none', fontSize: '12px' }}>
                          {t('store.aiCopy.panel.descriptionOnly')}
                        </Button>
                      </>
                    )}
                    <Button
                      size="small"
                      onClick={aiCopy.onDiscard}
                      disabled={!aiCopyCanAct || aiCopy.acceptPending || aiCopy.discardPending || aiCopy.generatePending}
                      sx={{ textTransform: 'none', fontSize: '12px', color: 'text.secondary' }}
                    >
                      {t('store.aiCopy.panel.discard')}
                    </Button>
                  </Box>
                  <Typography sx={{ fontSize: '11.5px', color: tokens.ink.muted }}>
                    {t('store.aiCopy.panel.acceptHint')}
                  </Typography>
                </Box>
              )}
            </Box>
  );
}

/** Approved bulk-AI spec suggestions and the specs in use (flag `bulk_ai_operations`); shared by both product details views. */
export function StoreAiSpecsBlock({
  aiSpecs,
  canManageStoreOverrides,
}: {
  aiSpecs: ProductOverridesAiSpecs;
  canManageStoreOverrides: boolean;
}) {
  const { t } = useAppTranslation();
  return (

            <Box
              data-testid="store-ai-specs"
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '12px', borderRadius: '10px', border: `1px solid ${tokens.line.hairline}`, bgcolor: tokens.surface.subtle }}
            >
              {Object.keys(aiSpecs.suggestions).length > 0 && (
                <>
                  <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: 'text.primary' }}>{t('store.aiSpecs.title')}</Typography>
                  <Typography sx={{ fontSize: '12px', lineHeight: 1.5, color: tokens.ink.muted }}>{t('store.aiSpecs.intro')}</Typography>
                  {!aiSpecs.canManage && <NeutralNote>{t('store.aiSpecs.ownerOnly')}</NeutralNote>}
                  {Object.entries(aiSpecs.suggestions).map(([label, value]) => (
                    <Box key={label} data-testid="store-ai-spec-row" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.faint }}>{label}</Typography>
                        <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>{value}</Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => aiSpecs.onUse(label)}
                        disabled={!canManageStoreOverrides || !aiSpecs.canManage || aiSpecs.pendingLabel !== null}
                        aria-label={`${t('store.aiSpecs.use')} ${label}`}
                        sx={{ textTransform: 'none', fontSize: '12px' }}
                      >
                        {t('store.aiSpecs.use')}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => aiSpecs.onDismiss(label)}
                        disabled={!canManageStoreOverrides || !aiSpecs.canManage || aiSpecs.pendingLabel !== null}
                        aria-label={`${t('store.aiSpecs.dismiss')} ${label}`}
                        sx={{ textTransform: 'none', fontSize: '12px', color: 'text.secondary' }}
                      >
                        {t('store.aiSpecs.dismiss')}
                      </Button>
                    </Box>
                  ))}
                </>
              )}
              {Object.keys(aiSpecs.used).length > 0 && (
                <>
                  <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: 'text.primary' }}>{t('store.aiSpecs.usedTitle')}</Typography>
                  <Typography sx={{ fontSize: '12px', color: tokens.ink.muted }}>{t('store.aiSpecs.usedHint')}</Typography>
                  {Object.entries(aiSpecs.used).map(([label, value]) => (
                    <Typography key={label} data-testid="store-ai-spec-used" sx={{ fontSize: '13px', color: 'text.primary' }}>
                      {label}: {value}
                    </Typography>
                  ))}
                </>
              )}
            </Box>
  );
}
